import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import CustomReportParams from './customReportParams.component';
import {
  getInitialStateFromReport,
  getInitialStateFromReportSet,
} from './customReportsMenu.initialState';
import ResetModal from './resetModal.component';
import ValidationConfirmationModal from './validationConfirmationModal.component';
import { useCustomReportValidation } from './useCustomReportValidation.hook';
import {
  updateLocalStorage,
  getLocalStorageKey,
} from '../../shared/customReports/localStorage';

import SecondaryMenu from '../../menu/secondaryMenu.component';
import Routes from '../../routes';

import {
  CustomReportTypes,
  defaultServerSideParams,
  CustomReportParamTypes,
} from '../../shared/customReports/constants';
import Select from '../../shared/reactSelect/select.component';
import { OptionsPropType } from '../../shared/propTypes/common';
import {
  customReportPropTypes,
} from '../../shared/customReports/propTypes';
import {
  getParamNamesForQuery,
  getParamsForEachReport,
} from '../../shared/customReports/helpers';
import Button from '../../shared/button.component';

import { displayCRExportFlagSelector } from '../../shared/store/selectors';

const EXPORT_FORMATS = {
  XLSX: 'xlsx',
  CSV: 'csv',
};

export const PureCustomReportsMenu = ({
  runCustomReport,
  exportCustomReport,
  selectCustomReport,

  isReportSetOpen,

  customReportsParamsOptions,
  customReportsDropdown,
  customReport,
  customReportSetReports,
  updateCustomReportQueryParams,
  customReportQueryParams,

  isBusy,
  isExportInProgress,
  resetCustomReport,
  gridApi,
  generateCustomReportExportName,
  isFetchingCustomReportParams,
  showResetCustomReportModal,
  hideResetCustomReportModal,
  resetCustomReportModalData,
  match,
  history,
  displayCRExportFlag,
  verifyParamOptionsQuery,
}) => {
  const [state, setState] = useState({});

  const {
    showValidationModal,
    validationMessage,
    runValidators,
    handleValidationCancel,
    handleValidationProceed,
  } = useCustomReportValidation({
    customReport,
    customReportSetReports,
    isReportSetOpen,
    customReportQueryParams,
    verifyParamOptionsQuery,
    paramMapping: state.mapping,
  });

  const initReport = useCallback(() => {
    if (isReportSetOpen) {
      const initialState = getInitialStateFromReportSet(customReportSetReports);
      setState(initialState);
      updateCustomReportQueryParams(initialState.queryParams);
    } else {
      const initialState = getInitialStateFromReport(customReport);
      setState(initialState);
      const visibleParams = customReport && customReport.params
        ? Object.values(customReport.params)
          .filter(param => param.type !== CustomReportParamTypes.validator)
          .map(param => param.name)
        : [];
      const initialQueryParams = visibleParams.reduce((acc, name) => {
        acc[name] = '';
        return acc;
      }, {});
      updateCustomReportQueryParams(initialQueryParams);
    }
  }, [
    customReport,
    customReportSetReports,
    isReportSetOpen,
    updateCustomReportQueryParams,
  ]);

  useEffect(() => {
    initReport();
  }, [
    initReport,
    isReportSetOpen,
    customReport,
    customReportSetReports,
    updateCustomReportQueryParams,
  ]);

  const areAllValuesSelected = useCallback(() => {
    if (!customReport) {
      return false;
    }

    if (isReportSetOpen) {
      const requiredParams = (state.uniqueParams || []).map(p => p.id);
      if (requiredParams.length === 0) {
        return true;
      }
      return requiredParams.every(id => Boolean(customReportQueryParams[id]));
    }

    const requiredParams = (state.uniqueParams || []).map(p => p.name);
    if (requiredParams.length === 0) {
      return true;
    }
    return requiredParams.every(name => Boolean(customReportQueryParams[name]));
  }, [customReport, isReportSetOpen, customReportQueryParams, state.uniqueParams]);

  const areSomeValuesSelected = () => {
    if (!customReport) {
      return false;
    }

    if (
      Object.keys(customReportQueryParams).length === 0
      || Object.values(customReportQueryParams).some(paramValue => paramValue)
    ) {
      return true;
    }

    return false;
  };

  const resetReportParameters = useCallback(() => {
    resetCustomReport();
    initReport();

    updateLocalStorage({
      keyname: getLocalStorageKey(customReport.reportSetId),
      state: {
        params: '',
      },
    });
  }, [customReport?.reportSetId, resetCustomReport, initReport]);

  const resetReportFilters = useCallback(() => {
    gridApi?.setFilterModel([]);
    gridApi?.setGridOption('pivotMode', false);
    gridApi?.applyColumnState({
      state: gridApi?.getColumns().map(({ colId }) => ({ colId })) || [],
      defaultState: {
        sort: null,
        rowGroup: null,
        pivot: null,
        pinned: null,
        hide: null,
        sortIndex: null,
        aggFunc: null,
        pivotIndex: null,
        rowGroupIndex: null,
      },
      applyOrder: true,
    });
    gridApi?.setColumnGroupState([]);

    if (customReport.customReportType === CustomReportTypes.REPORT_SET) {
      customReportSetReports.forEach(({ reportSetId }) => {
        updateLocalStorage({
          keyname: getLocalStorageKey(reportSetId, customReport.reportSetId),
          state: {
            filterModel: '',
            columnState: '',
            columnGroupState: '',
            isPivotMode: '',
          },
        });
      });
    } else {
      updateLocalStorage({
        keyname: getLocalStorageKey(customReport.reportSetId),
        state: {
          filterModel: '',
          columnState: '',
          columnGroupState: '',
          isPivotMode: '',
        },
      });
    }
  }, [customReport, customReportSetReports, gridApi]);

  const withAllValuesSelected = useCallback(strategy => () => {
    const { mapping } = state;

    if (areAllValuesSelected()) {
      if (isReportSetOpen) {
        const queries = getParamsForEachReport({
          reports: customReportSetReports,
          queryParams: customReportQueryParams,
          mapping,
          generateFileName: generateCustomReportExportName,
        });

        return strategy({
          reportId: customReport.reportSetId,
          queries,
          customReportType: customReport.customReportType,
          name: customReport.reportSetName,
          params: {
            ...(customReport.serverSide ? defaultServerSideParams : {}),
            ...customReportQueryParams,
          },
        });
      }

      const paramNames = getParamNamesForQuery(customReport.sql);

      const params = {
        ...(customReport.serverSide ? defaultServerSideParams : {}),
        ...pick(customReportQueryParams, paramNames),
      };
      return strategy({
        reportId: customReport.reportSetId,
        params,
        name: customReport.reportSetName,
        pillName: customReport.reportSetName,
        customReportType: customReport.customReportType,
      });
    }
  }, [
    areAllValuesSelected,
    isReportSetOpen,
    customReportSetReports,
    customReportQueryParams,
    generateCustomReportExportName,
    customReport,
    state,
  ]);

  useEffect(() => {
    if (customReportsDropdown.length && match.params?.customReportId) {
      selectCustomReport(match.params?.customReportId);
    }
  }, [selectCustomReport, customReportsDropdown, match.params?.customReportId]);

  const onCustomReportChange = useCallback(
    option => {
      if (
        !option
        || (customReport && customReport.reportSetId === option.value)
      ) {
        return;
      }
      history.push(Routes.reports.CUSTOM_REPORT.replace(/:customReportId/, option.value));
      selectCustomReport(option.value);
    },
    [selectCustomReport, history, customReport]
  );

  useEffect(() => {
    if (customReport) {
      const reportSettings = JSON.parse(
        window.localStorage.getItem(
          getLocalStorageKey(customReport.reportSetId)
        )
      );

      if (reportSettings?.params) {
        updateCustomReportQueryParams(JSON.parse(reportSettings?.params));
      }
    }
  }, [customReport, customReportSetReports, updateCustomReportQueryParams]);

  const updateQueryParams = useCallback(
    updatedQueryParams => {
      const params = {
        ...customReportQueryParams,
        ...updatedQueryParams,
      };

      updateCustomReportQueryParams(params);

      updateLocalStorage({
        keyname: getLocalStorageKey(customReport.reportSetId),
        state: {
          params: JSON.stringify(params),
        },
      });
    },
    [updateCustomReportQueryParams, customReport, customReportQueryParams]
  );

  const { uniqueParams } = state;

  const handleViewReport = useCallback(() => {
    if (areAllValuesSelected()) {
      runValidators(() => {
        withAllValuesSelected(runCustomReport)();
      });
    }
  }, [areAllValuesSelected, runValidators, withAllValuesSelected, runCustomReport]);

  const exportWithFormat = useCallback(format => () => {
    if (areAllValuesSelected()) {
      runValidators(() => {
        withAllValuesSelected(payload => exportCustomReport(payload, format))();
      });
    }
  }, [areAllValuesSelected, runValidators, withAllValuesSelected, exportCustomReport]);

  const handleExportToExcel = exportWithFormat(EXPORT_FORMATS.XLSX);
  const handleExportToCsv = exportWithFormat(EXPORT_FORMATS.CSV);

  return (
    <SecondaryMenu>
      <Select
        className="select-custom-reports"
        clearable={false}
        options={customReportsDropdown}
        value={customReport && customReport.reportSetId}
        placeholder="Custom Report"
        onChange={onCustomReportChange}
        virtualized
      />
      {customReport && customReportQueryParams ? (
        <CustomReportParams
          updateQueryParams={updateQueryParams}
          customReportsParamsOptions={customReportsParamsOptions}
          params={uniqueParams}
          queryParams={customReportQueryParams}
          isLoading={isFetchingCustomReportParams}
        />
      ) : null}
      <div className="custom-reports-buttons">
        <Button
          disabled={isBusy || !areAllValuesSelected()}
          onClick={handleViewReport}
        >
          View
        </Button>
        {displayCRExportFlag ? (
          <>
            <Button
              disabled={isBusy || !areAllValuesSelected()}
              onClick={handleExportToExcel}
              kind="secondary"
              isLoading={isExportInProgress}
            >
              Export to Excel
            </Button>
            <Button
              disabled={isBusy || !areAllValuesSelected()}
              onClick={handleExportToCsv}
              kind="secondary"
              isLoading={isExportInProgress}
            >
              Export to CSV
            </Button>
          </>
        ) : null}
        <Button
          disabled={isBusy || !areSomeValuesSelected()}
          onClick={showResetCustomReportModal}
          kind="secondary"
        >
          Reset
        </Button>
      </div>
      <ResetModal
        resetReportParameters={resetReportParameters}
        resetReportFilters={resetReportFilters}
        hideModal={hideResetCustomReportModal}
        isVisible={resetCustomReportModalData?.show}
      />
      <ValidationConfirmationModal
        isVisible={showValidationModal}
        validationMessage={validationMessage}
        onCancel={handleValidationCancel}
        onProceed={handleValidationProceed}
      />
    </SecondaryMenu>
  );
};

PureCustomReportsMenu.propTypes = {
  resetCustomReport: PropTypes.func.isRequired,
  runCustomReport: PropTypes.func.isRequired,
  exportCustomReport: PropTypes.func.isRequired,

  selectCustomReport: PropTypes.func.isRequired,
  updateCustomReportQueryParams: PropTypes.func.isRequired,
  showResetCustomReportModal: PropTypes.func.isRequired,
  hideResetCustomReportModal: PropTypes.func.isRequired,

  isReportSetOpen: PropTypes.bool.isRequired,
  customReportQueryParams: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ])).isRequired,
  resetCustomReportModalData: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ])).isRequired,

  customReportsParamsOptions: PropTypes.objectOf(OptionsPropType),
  customReportsDropdown: OptionsPropType,
  customReport: customReportPropTypes,
  customReportSetReports: PropTypes.arrayOf(customReportPropTypes),

  isBusy: PropTypes.bool,
  isExportInProgress: PropTypes.bool.isRequired,
  gridApi: PropTypes.shape({
    setFilterModel: PropTypes.func,
    setGridOption: PropTypes.func,
    applyColumnState: PropTypes.func,
    getColumns: PropTypes.func,
    setColumnGroupState: PropTypes.func,
  }),

  generateCustomReportExportName: PropTypes.func.isRequired,
  isFetchingCustomReportParams: PropTypes.bool.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      customReportId: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  displayCRExportFlag: PropTypes.bool.isRequired,
  verifyParamOptionsQuery: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  displayCRExportFlag: displayCRExportFlagSelector(state),
});

export default connect(mapStateToProps)(withRouter(PureCustomReportsMenu));

